import PageBreadcrumb from "../../components/common/CustomBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import { useCallback, useEffect, useRef, useState, type UIEvent } from "react";
import DeckService from "../../services/decks.service";
import { isValidURL } from "@/utils/validation";
import FlashcardService from "@/services/flashcard.service";
import { toast } from "react-toastify";
import RecommendDecksService from "@/services/recommend-decks";
import { useTranslation } from "react-i18next";

export default function CreateTrendingDecks() {
    const DECKS_LIMIT = 20;
    type RecommendDeckRow = { id: number; deckId: string; image: string; isNew?: boolean };
    type RecommendDeckSnapshot = {
        data: RecommendDeckRow[];
        checkFirst: boolean;
        actionMode: "create" | "edit" | null;
    };
    const createEmptyRow = (id: number): RecommendDeckRow => ({ id, deckId: "", image: "" });

    const [checkFirst, setCheckFirst] = useState(false);
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [decks, setDecks] = useState<any[]>([]);
    const [deckPage, setDeckPage] = useState(1);
    const [deckSearch, setDeckSearch] = useState("");
    const [deckHasMore, setDeckHasMore] = useState(true);
    const [deckLoading, setDeckLoading] = useState(false);
    const [openSelectId, setOpenSelectId] = useState<number | null>(null);
    const [data, setData] = useState<RecommendDeckRow[]>([createEmptyRow(1)]);
    const [actionMode, setActionMode] = useState<"create" | "edit" | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [imageSrc, setImageSrc] = useState<any>("");
    const deckRequestSeqRef = useRef(0);
    const nextRowIdRef = useRef(2);
    const openSelectRef = useRef<HTMLDivElement | null>(null);
    const historyRef = useRef<RecommendDeckSnapshot[]>([]);

    const pushHistory = useCallback(() => {
        historyRef.current = [
            ...historyRef.current,
            {
                data: data.map((item) => ({ ...item })),
                checkFirst,
                actionMode,
            },
        ];
    }, [actionMode, checkFirst, data]);

    const handleCancel = () => {
        const previousState = historyRef.current[historyRef.current.length - 1];
        if (!previousState) {
            return;
        }

        historyRef.current = historyRef.current.slice(0, -1);
        setData(previousState.data);
        setCheckFirst(previousState.checkFirst);
        setActionMode(previousState.actionMode);
        setOpenSelectId(null);
    };

    const handleOpenMedia = (src: string) => {
        setImageSrc(src);
        setIsModalOpen(true);
    };

    const markDecksDisabled = useCallback((deckList: any[], selectedData = data) => {
        const selectedDeckIds = selectedData.map((item: any) => item.deckId).filter(Boolean);
        return deckList.map((deck: any) => ({
            ...deck,
            disabled: selectedDeckIds.includes(deck._id),
        }));
    }, [data]);

    const fetchDecks = async (
        page = 1,
        search = "",
        append = false,
        selectedDataOverride?: RecommendDeckRow[]
    ) => {
        try {
            setDeckLoading(true);
            const currentRequest = ++deckRequestSeqRef.current;
            const response = await DeckService.getDecks({ page, limit: DECKS_LIMIT, search });

            if (currentRequest !== deckRequestSeqRef.current) {
                return;
            }

            const incomingDecks: any[] = response.data.deck || [];
            const totalCount = response.data.count || 0;

            setDecks((prev) => {
                const mergedDecks = append
                    ? [...prev, ...incomingDecks.filter((deck) => !prev.some((prevDeck) => prevDeck._id === deck._id))]
                    : incomingDecks;

                return markDecksDisabled(mergedDecks, selectedDataOverride || data);
            });

            setDeckPage(page);
            setDeckHasMore(totalCount ? page * DECKS_LIMIT < totalCount : incomingDecks.length >= DECKS_LIMIT);
        } catch (err) {
            console.error("Error fetching decks:", err);
        } finally {
            setDeckLoading(false);
        }
    };

    const resolveDeckImage = async (image: string) => {
        if (!image) return "";
        if (isValidURL(image)) return image;
        return fetchSignedUrl(image);
    };

    const fetchRecommendDecks = async () => {
        try {
            const response = await RecommendDecksService.getTrendingDecks();
            const recommendDecks: any = response.data.data || [];
            for (const recommendDeck of recommendDecks) {
                if (recommendDeck.image) {
                    recommendDeck.image = isValidURL(recommendDeck.image) ? recommendDeck.image : await fetchSignedUrl(recommendDeck.image);
                } else {
                    recommendDeck.image = "";
                }
            }
            let updatedData: RecommendDeckRow[] = [];
            if (recommendDecks.length === 0) {
                updatedData = [createEmptyRow(1)];
            } else {
                updatedData = recommendDecks.map((item: any, index: number) => ({
                    id: index + 1,
                    deckId: item.deckId || item._id || "",
                    image: item.image || "",
                    isNew: false,
                }));
            }

            nextRowIdRef.current = updatedData.reduce((maxId, item) => Math.max(maxId, item.id), 0) + 1;
            setData(updatedData);
            if (recommendDecks.length > 0) {
                setCheckFirst(false);
            } else {
                setCheckFirst(true);
            }

            await fetchDecks(1, "", false, updatedData);
        } catch (err) {
            console.error("Error fetching decks:", err);
        }
    };

    const fetchSignedUrl = async (fileKey: string): Promise<any> => {
        try {
            const res = await FlashcardService.getFile({ fileKey });
            if (res?.data?.status == 200) {
                return res?.data?.fileUrl;
            }
        } catch (err) {
            console.error("Error fetching signed URL:", err);
            return '';
        }
    };

    useEffect(() => {
        fetchRecommendDecks();
    }, []);

    useEffect(() => {
        setDecks((prev) => markDecksDisabled(prev));
    }, [markDecksDisabled]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openSelectRef.current && !openSelectRef.current.contains(event.target as Node)) {
                setOpenSelectId(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleDeckChange = async (rowId: number, value: string) => {
        pushHistory();
        const selectedRow = data.find((dt: any) => dt.id === rowId);
        const isNewRow = Boolean(selectedRow?.isNew);
        const selectedDeck = decks.find((deck: any) => deck._id === value);
        const updatedQuestions = data.map((dt: any) =>
            dt.id === rowId ? { ...dt, deckId: value, image: "" } : dt
        );
        setData(updatedQuestions);
        setActionMode(isNewRow ? "create" : "edit");
        setOpenSelectId(null);

        if (selectedDeck?.image) {
            const image = await resolveDeckImage(selectedDeck.image);
            setData((prev) => prev.map((dt: any) =>
                dt.id === rowId && dt.deckId === value ? { ...dt, image } : dt
            ));
        }
    };

    const openDeckSelect = async (rowId: number) => {
        const selectedRow = data.find((dt: any) => dt.id === rowId);
        setOpenSelectId(rowId);
        setDeckSearch("");
        setDeckPage(1);
        setDeckHasMore(true);
        if (selectedRow?.isNew) {
            setActionMode("create");
        }
        await fetchDecks(1, "", false);
    };

    const handleDeckSearchChange = async (value: string) => {
        setDeckSearch(value);
        setDeckPage(1);
        setDeckHasMore(true);
        await fetchDecks(1, value, false);
    };

    const handleDeckScroll = async (e: UIEvent<HTMLUListElement>) => {
        const target = e.currentTarget;
        if (
            target.scrollTop + target.clientHeight >= target.scrollHeight - 10 &&
            !deckLoading &&
            deckHasMore
        ) {
            await fetchDecks(deckPage + 1, deckSearch, true);
        }
    };

    const handleCreate = () => {
        setIsLoading(true);
        let continueFnc = true;
        for (const it of data) {
            if (!it.deckId) {
                continueFnc = false;
            }
        }
        if (continueFnc) {
            const deckIdsData = data.map((it: any) => it.deckId);
            RecommendDecksService.createTrendingDecks({ deckIds: deckIdsData })
                .then((data: any) => {
                    if (data.data.status === 200) {
                        toast.success("Create Successfully!");
                        setIsLoading(false);
                    } else {
                        toast.error("Create Failed!");
                        setIsLoading(false);
                    }
                })
                .catch((errors) => {
                    setIsLoading(false);
                    toast.error("Create Failed!");
                });
        } else {
            setIsLoading(false);
            toast.error("Please select a deck");
        }
    };


    const handleAddMore = () => {
        pushHistory();
        const currentQuestions = data || [];
        const newQuestion = createEmptyRow(nextRowIdRef.current);
        newQuestion.isNew = true;
        nextRowIdRef.current += 1;
        const updatedQuestions = [...currentQuestions, newQuestion];
        setData(updatedQuestions);
        setCheckFirst(true);
    };

    const handleRemove = (id: any) => {
        pushHistory();
        const updatedQuestions = data.filter((q: any) => q.id !== id);
        setData(updatedQuestions);
        setCheckFirst(true);
    };

    return (
        <div>
            <PageMeta
                title="VIYUME - VIYUME Dashboard"
                description=""
            />
            <div className="mx-auto max-w-[80%] grid grid-cols-1 gap-6">
                <div className="space-y-6">
                    <ComponentCard title="">
                        {data.map((dt: any, i: number) => (
                            <div key={dt.id}>
                                <div className="flex items-center justify-between">
                                    <Label>{t('Select Deck')} {i + 1}</Label>
                                    {data.length > 1 && (
                                        <button
                                            type="button"
                                            className="inline-flex items-center px-3 py-1 border border-red-400 shadow-sm text-xs font-medium rounded-md text-red-800 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                            onClick={() => handleRemove(dt.id)}
                                        >
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 3h4a1 1 0 011 1v1H9V4a1 1 0 011-1z" />
                                            </svg>
                                            {t('Remove')}
                                        </button>
                                    )}
                                </div>
                                {checkFirst || dt.deckId ? (
                                    <div ref={openSelectId === dt.id ? openSelectRef : null} className="relative mt-4">
                                        <button
                                            type="button"
                                            className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-left text-sm text-gray-700 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                                            onClick={() => openDeckSelect(dt.id)}
                                        >
                                            {decks.find((deck: any) => deck._id === dt.deckId)?.title || t("Select an option")}
                                        </button>

                                        {openSelectId === dt.id && (
                                            <div className="absolute z-30 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
                                                <div className="border-b border-gray-200 p-2 dark:border-gray-700">
                                                    <input
                                                        value={deckSearch}
                                                        onChange={(e) => handleDeckSearchChange(e.target.value)}
                                                        placeholder={t("Search deck...")}
                                                        className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:border-brand-300 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                                    />
                                                </div>

                                                <ul className="max-h-60 overflow-y-auto" onScroll={handleDeckScroll}>
                                                    {decks.map((deck: any) => (
                                                        <li
                                                            key={deck._id}
                                                            onClick={() => !deck.disabled && handleDeckChange(dt.id, deck._id)}
                                                            className={`cursor-pointer px-4 py-2 text-sm dark:text-white/90 ${deck.disabled
                                                                ? "cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500"
                                                                : "hover:bg-gray-100 dark:hover:bg-gray-800"
                                                                }`}
                                                        >
                                                            {deck.title}
                                                        </li>
                                                    ))}

                                                    {deckLoading && (
                                                        <li className="px-4 py-2 text-sm text-gray-500">{t("Loading")}...</li>
                                                    )}

                                                    {!deckLoading && decks.length === 0 && (
                                                        <li className="px-4 py-2 text-sm text-gray-500">{t("No decks found")}</li>
                                                    )}

                                                    {!deckLoading && !deckHasMore && decks.length > 0 && (
                                                        <li className="px-4 py-2 text-sm text-gray-400">{t("No more decks")}</li>
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ) : null}

                                {dt.image && (
                                    <div className="mt-4 relative inline-flex items-center gap-2">
                                        <img
                                            src={dt.image}
                                            alt="Preview"
                                            className="max-w-xs rounded-lg shadow-lg"
                                            onClick={() => handleOpenMedia(dt.image)}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                        <hr className="my-6 border-gray-300 dark:border-gray-700" />
                        <div className="flex justify-between items-center">
                            <button
                                type="button"
                                className="inline-flex items-center px-4 py-2 border border-yellow-400 shadow-sm text-sm font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                onClick={handleAddMore}
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                </svg>
                                {t('Add More')}
                            </button>

                            {actionMode && (
                                <div className="flex">
                                    <button
                                        type="button"
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        onClick={handleCancel}
                                    >
                                        {t('Cancel')}
                                    </button>
                                    <div className="mx-2"></div>
                                    <button
                                        type="button"
                                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        onClick={() => {
                                            if (!isLoading) {
                                                handleCreate()
                                            }
                                        }}
                                    >
                                        {isLoading ? (
                                            <div className="flex items-center justify-center gap-2">
                                                <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                {actionMode === "edit" ? t('Editing') : t('Creating')}...
                                            </div>
                                        ) : (
                                            <>
                                                {actionMode === "edit" ? t('Edit') : t('Create')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                        {isModalOpen && (
                            <>
                                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-70">
                                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl p-4 relative m-4">
                                        <button
                                            onClick={() => setIsModalOpen(false)}
                                            className="absolute -top-2 -right-2 z-50 bg-gray-800 text-white hover:bg-gray-900 w-8 h-8 rounded-full flex items-center justify-center text-lg transition-colors"
                                        >
                                            &times;
                                        </button>
                                        <div className="h-[80vh] flex items-center justify-center">
                                            <img
                                                src={imageSrc}
                                                alt="Preview"
                                                className="max-h-full max-w-full object-contain rounded-md"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </ComponentCard>
                </div>
            </div>
        </div>
    );
}

